/**
 * Canonical post/thread permalinks and share helpers.
 * Workplace: /o/:orgId/p/:postId
 * Social:    /p/:postId
 */

import { markdownToPlain } from "@/lib/markdownPlain";

export function permalinkForPost({ orgId, postId }) {
  if (orgId == null || postId == null) return null;
  return `/o/${encodeURIComponent(String(orgId))}/p/${encodeURIComponent(String(postId))}`;
}

/** Canonical social-plane post/thread permalink. */
export function permalinkForSocialPost({ postId }) {
  if (postId == null) return null;
  return `/p/${encodeURIComponent(String(postId))}`;
}

/**
 * Stable attachment payload for DMs / notifications (prefer ids over raw href parsing).
 */
export function postAttachment({ orgId, postId, title, kind }) {
  if (orgId == null || postId == null) return null;
  const attachment = {
    type: "post",
    org_id: String(orgId),
    post_id: Number(postId),
  };
  if (title != null && String(title).trim()) {
    const plain = markdownToPlain(title) || String(title).trim();
    if (plain) attachment.title = plain;
  }
  if (kind === "post" || kind === "card") {
    attachment.kind = kind;
  }
  return attachment;
}

/**
 * Parse the first workplace post permalink in text for this org.
 * Matches absolute URLs and paths: /o/:orgId/p/:postId
 * @returns {{ attachment: object, matchedUrl: string } | null}
 */
export function extractPostPermalinkFromText(text, orgId) {
  if (!text || orgId == null) return null;
  const re = /(?:https?:\/\/[^\s)]+)?\/o\/([^/\s?#)]+)\/p\/(\d+)/gi;
  let match;
  while ((match = re.exec(String(text))) !== null) {
    let parsedOrg;
    try {
      parsedOrg = decodeURIComponent(match[1]);
    } catch {
      parsedOrg = match[1];
    }
    if (String(parsedOrg) !== String(orgId)) continue;
    const postId = Number(match[2]);
    if (!Number.isFinite(postId)) continue;
    const attachment = postAttachment({ orgId, postId, kind: "post" });
    if (!attachment) continue;
    return { attachment, matchedUrl: match[0] };
  }
  return null;
}

/**
 * If content is only the permalink (or a markdown link wrapping it), return empty string.
 */
export function stripLonePostPermalink(content, matchedUrl) {
  const trimmed = String(content || "").trim();
  if (!trimmed || !matchedUrl) return trimmed;
  if (trimmed === matchedUrl.trim()) return "";
  const pathOnly = matchedUrl.replace(/^https?:\/\/[^/]+/, "");
  if (trimmed === pathOnly) return "";
  const md = trimmed.match(/^\[([^\]]*)\]\(([^)]+)\)$/);
  if (md && md[2].includes(pathOnly)) return "";
  return String(content);
}

/** Kanban board deep link (view preference — not thread identity). */
export function boardHrefForCard({ orgId, teamId, postId, sprintId } = {}) {
  if (orgId == null || teamId == null || postId == null) return null;
  const params = new URLSearchParams({
    org_id: String(orgId),
    tab: "kanban",
    card: String(postId),
  });
  if (sprintId != null && sprintId !== "") {
    params.set("sprint_id", String(sprintId));
  }
  return `/teams/${encodeURIComponent(String(teamId))}?${params.toString()}`;
}

/** DOM id for a kanban card (scroll-into-view on board deep link). */
export function kanbanCardAnchorId(postId) {
  if (postId == null) return null;
  return `kanban-card-${postId}`;
}

/** DOM id for a feed post card (scroll-into-view on return). */
export function feedPostAnchorId(postId) {
  if (postId == null) return null;
  return `post-${postId}`;
}

/** Append #post-:id to a return path (strips any existing hash). */
export function pathWithPostAnchor(path, postId) {
  if (!path) return path;
  const anchor = feedPostAnchorId(postId);
  if (!anchor) return path;
  const base = String(path).split("#")[0];
  return `${base}#${anchor}`;
}

/**
 * Optional navigation query on a permalink.
 * @returns {{ fromCatchup: boolean, from: string | null, returnPath: string | null }}
 */
export function parsePostPermalinkSearch(search) {
  const params = new URLSearchParams(
    typeof search === "string" ? search : search?.toString?.() || ""
  );
  const from = params.get("from");
  const returnRaw = params.get("return");
  let returnPath = null;
  if (returnRaw) {
    try {
      returnPath = decodeURIComponent(returnRaw);
    } catch {
      returnPath = returnRaw;
    }
    // Only allow same-origin relative paths
    if (!returnPath.startsWith("/") || returnPath.startsWith("//")) {
      returnPath = null;
    }
  }
  return {
    from,
    fromCatchup: from === "catchup",
    returnPath,
  };
}

/** Append return= (and optional from=) for in-app back navigation. */
export function withReturnQuery(href, returnPath, from) {
  if (!href) return href;
  try {
    const url = new URL(href, window.location.origin);
    if (from) {
      url.searchParams.set("from", from);
    }
    if (returnPath) {
      url.searchParams.set("return", returnPath);
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}

/**
 * Build a social thread URL with return= and optional workplace org context.
 * When basePath is set (workplace Circa Social), nest under that prefix.
 * @param {{ postId: string|number, returnPath?: string|null, orgId?: string|null, basePath?: string|null }} opts
 */
export function socialThreadHref({ postId, returnPath, orgId, basePath }) {
  if (postId == null) return null;
  const href = basePath
    ? `${String(basePath).replace(/\/$/, "")}/p/${encodeURIComponent(String(postId))}`
    : permalinkForSocialPost({ postId });
  if (!href) return null;
  try {
    const url = new URL(
      withReturnQuery(href, returnPath) || href,
      window.location.origin
    );
    if (orgId != null && String(orgId).trim()) {
      url.searchParams.set("org", String(orgId).trim());
    }
    return `${url.pathname}${url.search}`;
  } catch {
    return href;
  }
}

/**
 * Only allow same-origin relative paths for return= (open-redirect safe).
 * @returns {string|null}
 */
export function safeReturnPath(raw) {
  if (raw == null) return null;
  const path = String(raw).trim();
  if (!path.startsWith("/") || path.startsWith("//")) return null;
  if (path.includes("://")) return null;
  return path;
}

/** Append from=catchup&return= for catch-up deep links. */
export function withCatchupReturn(href, returnPath) {
  return withReturnQuery(href, returnPath, "catchup");
}

/**
 * Comment deep-link / drill-in query on a post permalink.
 * `comment` = scroll + highlight in the full thread.
 * `focus` = show that comment as the reply-list root (mobile drill-in).
 * @returns {{ commentId: string | null, focusId: string | null }}
 */
export function parseCommentThreadSearch(search) {
  const params = new URLSearchParams(
    typeof search === "string" ? search : search?.toString?.() || ""
  );
  return {
    commentId: params.get("comment"),
    focusId: params.get("focus"),
  };
}

/**
 * Set or clear `focus` on an href/path, preserving other query params.
 * @param {string} href
 * @param {string|number|null|undefined} focusId - omit/null/undefined removes focus
 */
export function withFocusQuery(href, focusId) {
  if (!href) return href;
  try {
    const url = new URL(href, window.location.origin);
    if (focusId == null || focusId === "") {
      url.searchParams.delete("focus");
    } else {
      url.searchParams.set("focus", String(focusId));
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

/**
 * Leave focus mode: drop `focus`, set `comment` for scroll/highlight restore.
 * Preserves from/return/org and other params.
 * @param {string} href
 * @param {string|number|null|undefined} commentId
 */
export function withFocusClearedToComment(href, commentId) {
  if (!href) return href;
  try {
    const url = new URL(href, window.location.origin);
    url.searchParams.delete("focus");
    if (commentId == null || commentId === "") {
      url.searchParams.delete("comment");
    } else {
      url.searchParams.set("comment", String(commentId));
    }
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return href;
  }
}

/**
 * Build pathname+search for navigate() from the current location, setting focus.
 * @param {{ pathname: string, search?: string }} location
 * @param {string|number} focusId
 */
export function locationWithFocus(location, focusId) {
  const base = `${location.pathname}${location.search || ""}`;
  return withFocusQuery(base, focusId);
}

/**
 * Build pathname+search for navigate() when leaving focus mode.
 * @param {{ pathname: string, search?: string }} location
 * @param {string|number} commentId
 */
export function locationWithFocusClearedToComment(location, commentId) {
  const base = `${location.pathname}${location.search || ""}`;
  return withFocusClearedToComment(base, commentId);
}
